import React, { Component } from 'react';
import { connect } from 'react-redux';
import clsx from 'clsx';
import { PropTypes } from 'prop-types';
import { Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Icon,
  Divider,
  Typography,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { LoadingView } from 'components';
import { StoreUtil } from 'doctivity-shared/utils';
import { withRouter, withIsMobile } from 'utils';

import { selectClient } from 'store/actions/appActions';
import { showConfirmationDialog } from 'store/actions/systemActions';
import { listClients } from 'store/actions/clientsActions';
import { logoutUser } from 'store/actions/loginActions';
import { Header } from './Header';
import logo from 'assets/images/logo_small.png';
import UpgradeableContext from './UpgradeableContext';

import 'react-chatbot-kit-extension/build/main.css';
import '../components/chatbot/custom.css';

import { DocChatBot } from '../components/chatbot/DocChatBot.js';
import { Outlet } from 'react-router-dom';

import AudioConversation from 'components/chatbot/AudioConversation';

const drawerWidth = 224;
const minimizedDrawerWidth = 100;

// Styled components using MUI v5 styled API
const LogoImg = styled('img')(({ theme, open }) => ({
  padding: 8,
  width: 84,
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  ...(open && {
    width: 192,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  }),
}));

const ContentBox = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  height: '100vh',
  overflow: 'auto',
  backgroundColor: '#fafafa',
  [theme.breakpoints.up('md')]: {
    position: 'absolute',
    left: minimizedDrawerWidth,
    right: 0,
  },
}));

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  border: 'none',
  overflowX: 'hidden',
  '& .MuiDrawer-paper': {
    backgroundColor: '#00A887',
    scrollbarWidth: 'thin',
    scrollbarColor: '#7F7F7F transparent',
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: minimizedDrawerWidth,
    ...(open && {
      boxShadow: '6px 0px 6px #00000029',
      width: drawerWidth,
    }),
  },
}));

const MobileDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    backgroundColor: '#00A887',
  },
}));

const StyledListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  paddingTop: 4,
  paddingBottom: 4,
  paddingLeft: 35,
  paddingRight: 0,
  color: '#fff',
  '&:hover': {
    '& *': {
      color: '#fff',
    },
    color: '#fff',
  },
  cursor: 'pointer',
  ...(selected && {
    color: '#fff',
    backgroundColor: theme.palette.primary.accent + '4E',
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: theme.palette.primary.accent + '4E',
    },
  }),
}));

const StyledListItemIcon = styled(Icon, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  paddingTop: 4,
  paddingBottom: 4,
  fontSize: 30,
  color: 'rgba(255, 255, 255, 0.5)',
  ...(selected && {
    color: '#fff',
  }),
}));

const ClientContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  maxWidth: minimizedDrawerWidth - 5 * parseInt(theme.spacing()),
  height: 24,
  backgroundColor: theme.palette.secondary.accent,
  borderBottomLeftRadius: 10,
  borderBottomRightRadius: 10,
  padding: theme.spacing(),
  paddingTop: 4,
  paddingBottom: 4,
  paddingLeft: theme.spacing(2),
  transition: theme.transitions.create('all', {
    easing: theme.transitions.easing.easeIn,
    duration: theme.transitions.duration.shorter,
  }),
  ...(open && {
    maxWidth: drawerWidth - 5 * parseInt(theme.spacing()),
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shorter,
    }),
  }),
}));

const SelectedWedgeIcon = styled(Icon)(({ theme, isDashboard }) => ({
  color: '#FAFAFA',
  marginRight: -17,
  fontSize: 40,
  position: 'absolute',
  right: 0,
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
  ...(isDashboard && {
    color: '#214E56',
  }),
}));

const StyledDivider = styled(Divider)({
  borderColor: '#05BE8D',
});

class AppNavigation extends Component {
  chatApi = null;
  micPermStatus = null;
  alreadyMovedToBottom = false;

  static contextType = UpgradeableContext;

  constructor(props) {
    super(props);

    this.state = {
      drawerOpen: false,
      clientMenuOpen: false,
      waitingForMouseExit: false,
      showChat: false,
      chatMinimized: false,
      showMicDialog: false,
      micChecking: false,
      micReason: 'prompt',
    };
  }

  handleMicPermissionChange = (status) => {
    const state = status?.state || this.micPermStatus?.state;
    if (state === 'prompt') {
      localStorage.removeItem('mic_requested');
      if (localStorage.getItem('mic_pref') === 'voice') {
        localStorage.removeItem('mic_pref');
      }
    }
    if (state === 'granted') {
      localStorage.setItem('mic_requested', 'yes');
    }
  };

  setupMicPermissionWatcher = async () => {
    try {
      if (!navigator.permissions?.query) return;
      const status = await navigator.permissions.query({ name: 'microphone' });
      this.micPermStatus = status;
      this.handleMicPermissionChange(status);
      status.onchange = () => this.handleMicPermissionChange(status);
    } catch {
      // no-op: not supported
    }
  };

  recheckMicPermissionOnFocus = async () => {
    try {
      if (!navigator.permissions?.query) return;
      const status = await navigator.permissions.query({ name: 'microphone' });
      this.handleMicPermissionChange(status);
    } catch {
      // no-op: not supported
    }
  };

  handleMinimizeChange = (min) => {
    this.setState({ chatMinimized: min });
  };

  isChatMinimized() {
    return (
      this.chatRef &&
      this.chatRef.state &&
      this.chatRef.state.minimized
    );
  }

  onChatBot = () => {
    if (!this.state.showChat) {
      this.setState({ showChat: true, expanded: true }, () => {
        this.alreadyMovedToBottom = false;
        this.toggleChatSize(true);
      });
      return;
    }
    this.chatApi?.toggleMinimize();
  };

  toggleChat = () => {
    let showIt = !this.state.showChat;

    const hasMessages =
      this.props.app &&
      this.props.app.chatbot &&
      Array.isArray(this.props.app.chatbot.messages) &&
      this.props.app.chatbot.messages.length > 0;

    let expanded = true;

    this.setState(
      { showChat: showIt, expanded },
      () => {
        if (showIt) {
          this.alreadyMovedToBottom = false;
          this.toggleChatSize(expanded);
          this.props.setClicked?.(true);
        } else {
          sessionStorage.removeItem('system_prompt');
          const audioActive =
            AudioConversation.getInstance().isConversationStarted();
          if (!audioActive) this.props.setClicked?.(false);
        }
      }
    );
  };

  toggleChatSize = (force) => {
    const expanded =
      typeof force === 'boolean' ? force : !this.state.expanded;

    this.setState({ expanded });

    const box = document.querySelector('.react-chatbot-kit-chat-container');
    if (!box) return;

    if (expanded) {
      box.style.height = '80%';
      box.style.width = '30%';
    } else if (this.alreadyMovedToBottom) {
      box.style.height = '80%';
      box.style.width = '30%';
    } else {
      box.style.height = '60%';
      box.style.width = '30%';
    }
  };

  componentDidMount() {
    const { user, clients, dispatch } = this.props;
    const { setOpenChat } = this.props;

    if (typeof setOpenChat === 'function') {
      setOpenChat(this.onChatBot);
    }

    if (this.context && this.context.setNavigationHandler) {
      this.context.setNavigationHandler(this.handleNavigation.bind(this));
    }

    if (user && user.id && StoreUtil.needsLoadNoCache(clients)) {
      dispatch(listClients());
    }

    this.setupMicPermissionWatcher();
    window.addEventListener('focus', this.recheckMicPermissionOnFocus);

    const audio = AudioConversation.getInstance();
    const st = audio.getState ? audio.getState() : { active: false, speaking: false, listening: false };
    this.setState({ audioActive: st.active, micSpeaking: st.speaking, micListening: st.listening });

    this.audioStateHandler = (e) => {
      const s = e.detail || {};
      this.setState({ audioActive: s.active, micSpeaking: s.speaking, micListening: s.listening });
    };
    window.addEventListener('audio-state', this.audioStateHandler);
  }

  componentWillUnmount() {
    window.removeEventListener('focus', this.recheckMicPermissionOnFocus);
    if (this.micPermStatus) {
      this.micPermStatus.onchange = null;
      this.micPermStatus = null;
    }
    window.removeEventListener('audio-state', this.audioStateHandler);
  }

  componentDidUpdate(props) {
    if (
      this.props.user &&
      (!props.user || this.props.user.id !== props.user.id)
    ) {
      this.props.dispatch(listClients());
    }
  }

  handleNavigation = async (props) => {
    console.log('AppNavigation: Navigation occurred!');
    console.log(this.props.router.location.pathname);

    const audio = AudioConversation.getInstance();
    if (audio.isConversationStarted()) {
      const abs = typeof window !== 'undefined'
        ? window.location.origin + this.props.router.location.pathname
        : this.props.router.location.pathname;
      audio.updateUrl(abs);
    }
  };

  onDrawerOpen = () => {
    this.setState({ drawerOpen: true });
  };

  onDrawerClose = () => {
    this.setState({ drawerOpen: false });
  };

  onMouseEnter = () => {
    if (!this.state.waitingForMouseExit) {
      this.onDrawerOpen();
    }
  };

  onMouseLeave = () => {
    this.setState({ waitingForMouseExit: false });
    this.onDrawerClose();
  };

  onSelectNavItem = () => {
    this.setState({ waitingForMouseExit: true });
    this.onDrawerClose();
  };

  onLogout = () => {
    const { dispatch, router } = this.props;
    router.navigate('/');
    sessionStorage.clear();
    dispatch(logoutUser());
  };

  onContactSupport = () => {
    const { dispatch } = this.props;

    dispatch(
      showConfirmationDialog({
        title: 'Doctivity Support',
        content: (
          <>
            <p>
              Support and documentation for Doctivity is available at our
              self-service knowledge base.
            </p>
            <p>
              <a
                href='https://doctivity-health.helpscoutdocs.com/'
                target='_blank'
                rel='noreferrer noopener'
                style={{ color: '#00A887' }}
              >
                https://doctivity-health.helpscoutdocs.com/
              </a>{' '}
              (Opens in New Tab)
            </p>
            <p>You may also email us at support@doctivityhealth.com</p>
          </>
        ),
        confirmLabel: 'Visit Knowledge Base',
        onConfirm: () => {
          window.open('https://doctivity-health.helpscoutdocs.com/', '_blank');
        },
        onCancel: () => {
          // do nothing
        },
      })
    );
  };

  setClient = (client) => {
    const { dispatch, selectedClient } = this.props;

    if (selectedClient !== client.id) {
      dispatch(selectClient(client.id));
    }
  };

  shouldShowMicDialog = async () => {
    if (localStorage.getItem('mic_pref') === 'keyboard') return false;
    if (localStorage.getItem('mic_requested') === 'yes') return false;

    try {
      if (navigator.permissions?.query) {
        const status = await navigator.permissions.query({ name: 'microphone' });
        return status.state === 'prompt';
      }
    } catch (_) { }

    return true;
  };

  requestMicAccess = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true },
        video: false
      });

      stream.getTracks().forEach((t) => t.stop());
      localStorage.setItem('mic_requested', 'yes');
      localStorage.setItem('mic_pref', 'voice');
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err?.name || 'NotAllowedError' };
    }
  };

  openChatOrAudio = async () => {
    this.setState({ micChecking: true, micReason: 'prompt' });
    const show = await this.shouldShowMicDialog();
    this.setState({ micChecking: false });

    if (show) {
      this.setState({ showMicDialog: true });
      return;
    }
    this.startChatNormally();
  };

  startChatNormally = () => {
    this.onChatBot();
  };

  startAudioOnly = () => {
    const audio = AudioConversation.getInstance();
    if (!audio.isConversationStarted()) {
      const { router, user, app, dispatch } = this.props;
      const chatbotState = this.props.app?.chatbot || { messages: [] };
      audio.startConversation(
        router,
        user,
        app.selectedClient,
        dispatch,
        chatbotState,
        () => ({ app: this.props.app })   // live getState for criteria injection
      );
    }
  };

  onUseAudio = async () => {
    localStorage.setItem('mic_pref', 'voice');
    const res = await this.requestMicAccess();
    this.setState({ showMicDialog: false });

    if (res.ok) {
      if (this.state.showChat) {
        this.setState({ showChat: false }, () => this.startAudioOnly());
      } else {
        this.startAudioOnly();
      }
    } else {
      const reason =
        res.error === 'NotFoundError' ? 'no-device' :
          res.error === 'NotAllowedError' ? 'blocked' :
            'prompt';
      this.setState({ showMicDialog: true, micReason: reason });
    }
  };

  onUseKeyboard = () => {
    localStorage.setItem('mic_pref', 'keyboard');
    this.setState({ showMicDialog: false });
    if (!this.state.showChat) this.startChatNormally();
  };

  onShowMicDialog = (reason) => {
    this.setState({ showMicDialog: true, micReason: reason || 'prompt' });
  };

  onLetsGo = async () => {
    const res = await this.requestMicAccess();
    this.setState({ showMicDialog: false });

    if (res.ok) {
      if (this.state.showChat) {
        this.setState({ showChat: false }, () => this.startAudioOnly());
      } else {
        this.startAudioOnly();
      }
    } else {
      localStorage.setItem('mic_pref', 'keyboard');
      this.setState({ showMicDialog: false });
      this.startChatNormally();
    }
  };

  render() {
    const { user, router, clients, app } = this.props;
    const { drawerOpen: _drawerOpen } = this.state;
    const drawerOpen = this.drawerOpenOrOnDashboard();

    if (!StoreUtil.isLoaded(clients)) {
      return <LoadingView />;
    }

    let client = null;
    if (app.selectedClient && StoreUtil.isLoaded(clients)) {
      const data = StoreUtil.getData(clients);
      client = data.find((c) => c.id === parseInt(app.selectedClient));
    }

    const dashboard = router.location.pathname === '/' ||
      router.location.pathname.startsWith('/dashboard');

    const drawerItems = (
      <>
        <StyledDivider />
        <List disablePadding>{this.getClientItems()}</List>
        <StyledDivider />
        <List disablePadding>{this.getExploreItems(client)}</List>
        {user.is_admin && (
          <>
            <StyledDivider />
            <List disablePadding>{this.getAdminItems()}</List>
            <StyledDivider />
            <List disablePadding>{this.getAIItems()}</List>
          </>
        )}

        {!user.is_admin && (
          <>
            <StyledDivider />
            <List disablePadding>{this.getAccountItems()}</List>
          </>
        )}
      </>
    );

    return (
      <>
        <Header
          onDrawerOpen={_drawerOpen ? this.onDrawerClose : this.onDrawerOpen}
        />

        {/* Mobile Drawer - shown on small screens */}
        <MobileDrawer
          variant='temporary'
          anchor='left'
          open={_drawerOpen}
          onClose={this.onDrawerClose}
          sx={{
            display: { xs: 'block', md: 'none' },
          }}
          ModalProps={{
            keepMounted: true,
          }}
        >
          {drawerItems}
        </MobileDrawer>

        {/* Desktop Drawer - shown on medium+ screens */}
        <StyledDrawer
          variant='permanent'
          open={drawerOpen}
          onMouseEnter={this.onMouseEnter}
          onMouseLeave={this.onMouseLeave}
          sx={{
            display: { xs: 'none', md: 'block' },
          }}
        >
          <>
            <Box
              sx={{
                minHeight: 111,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Link to='/dashboard'>
                <LogoImg
                  src={logo}
                  open={drawerOpen}
                  alt='Logo'
                />
              </Link>
            </Box>
            {drawerItems}
          </>
        </StyledDrawer>

        <ContentBox>
          <Outlet />
        </ContentBox>

        {this.state.showChat && (
          <DocChatBot
            provideApi={(api) => (this.chatApi = api)}
            onMinimizeChange={(min) => this.setState({ chatMinimized: min })}
            toggleChat={this.toggleChat}
            toggleChatSize={this.toggleChatSize}
            setOpenChat={this.props.setOpenChat}
            user={user}
            selectedClient={app.selectedClient}
            prevstate={this.state}
            expanded={this.state.expanded}
            keyboardOnly={localStorage.getItem('mic_pref') === 'keyboard'}
            onShowMicDialog={(reason) => this.onShowMicDialog(reason)}
            audioActive={this.state.audioActive}
            micSpeaking={this.state.micSpeaking}
            micListening={this.state.micListening}
          />
        )}

        <Dialog
          open={this.state.showMicDialog}
          onClose={this.onUseKeyboard}
          maxWidth='sm'
          fullWidth
          sx={{ zIndex: 10004 }}
        >
          <DialogTitle>
            {this.state.micReason === 'no-device' ? 'No Microphone Detected' :
              this.state.micReason === 'blocked' ? 'Microphone Access Blocked' :
                'Microphone Access'}
          </DialogTitle>
          <DialogContent dividers>
            {this.state.micReason === 'no-device' && (
              <Typography variant='body1' paragraph>
                We didn't detect any microphone on this device. Plug in a mic or select a different input in your system settings. You can continue using the keyboard, or try audio again after connecting a mic.
              </Typography>
            )}
            {this.state.micReason === 'blocked' && (
              <Typography variant='body1' paragraph>
                Microphone access is blocked in your browser/site settings. Please allow microphone access and then choose "Use Audio".
              </Typography>
            )}
            {this.state.micReason === 'user-disabled' && (
              <Typography>
                You chose keyboard-only earlier. You can switch to voice now by allowing microphone access.
              </Typography>
            )}
            {(this.state.micReason === 'prompt' || !this.state.micReason) && (
              <Typography>
                Doctivity's AI works best with voice. Click "Use Audio" to allow your microphone.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.onUseKeyboard} color='primary'>
              I'll Use My Keyboard
            </Button>
            <Button onClick={this.onUseAudio} variant='contained' color='primary'>
              Use Audio
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  getItem = (
    path,
    icon,
    name,
    disabled = false,
    handler = null,
    alternativePaths = [],
    iconStyles = {}
  ) => {
    const { router } = this.props;

    const drawerOpen = this.drawerOpenOrOnDashboard();

    const isDashboard =
      router.location.pathname === '/' ||
      router.location.pathname.startsWith('/dashboard');
    const itemSelected =
      (path === '/dashboard' && router.location.pathname === '/') ||
      router.location.pathname.startsWith(path) ||
      alternativePaths.some((p) =>
        router.location.pathname.startsWith(p)
      );

    const MenuIcon = typeof icon === 'string' ? () => (
      <StyledListItemIcon selected={itemSelected}>
        {icon}
      </StyledListItemIcon>
    ) : icon;

    return (
      <UpgradeableContext.Consumer>
        {(ctx) => (
          <StyledListItem
            button
            key={path}
            component={handler ? undefined : Link}
            to={handler ? undefined : path}
            disabled={disabled}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              ctx.setHasNavigated(true);
              if (handler) {
                handler();
              } else {
                this.props.router.navigate(path);
              }
              this.onSelectNavItem();
            }}
            selected={itemSelected}
          >
            <>
              <ListItemIcon
                key='icon'
                sx={{
                  minWidth: drawerOpen ? '56px' : 0,
                  ...iconStyles
                }}
              >
                <MenuIcon />
              </ListItemIcon>
              {drawerOpen && (
                <ListItemText
                  key='text'
                  primary={name}
                  sx={{ marginLeft: -2 }}
                  primaryTypographyProps={
                    itemSelected ? { fontWeight: 700 } : { fontWeight: 300 }
                  }
                />
              )}
              {itemSelected && (isDashboard || !drawerOpen) && (
                <SelectedWedgeIcon isDashboard={isDashboard}>
                  arrow_left
                </SelectedWedgeIcon>
              )}
            </>
          </StyledListItem>
        )}
      </UpgradeableContext.Consumer>
    );
  };

  getExploreItems = (client) => {
    const drawerOpen = this.drawerOpenOrOnDashboard();
    const items = [
      this.getItem('/dashboard', 'dashboard_icon', 'Dashboard', false, null),
      this.getItem('/notes', 'speaker_notes', 'Activity Notes'),
      this.getItem('/labels', 'sell', 'Labels', false, null, [
        '/label_types',
      ]),
      this.getItem('/providers', 'people', 'Providers', false, null, [
        '/providers',
        '/recent_providers',
        '/referring_providers',
      ]),
      this.getItem('/contacts', 'contact_phone', 'Contacts'),
      this.getItem('/locations', 'place', 'Locations', false, null, [
        '/locations',
        '/recent_locations',
        '/favorite_locations',
        '/location_changes',
      ]),
      this.getItem(
        '/organizations',
        'apartment',
        'Organizations',
        false,
        null,
        ['/organizations', '/affiliated_organizations', '/recent_organizations']
      ),
    ];
    if (client && client.has_claims_access) {
      items.splice(
        2,
        0,
        this.getItem('/claims', 'leaderboard', 'Market Data', false, null, [], {
          transform: 'rotate(90deg)',
          transformOrigin: drawerOpen ? '27% 50%' : '50% 50%',
        })
      );
    }

    items.push(
      this.getItem('/visits/', 'swap_horiz', 'Visits & Referrals'),
      this.getItem('/documents', 'description', 'Documents')
    );
    return items;
  };

  getAdminItems() {
    return [
      this.getItem('/users', 'face', 'Users'),
      this.getItem('/clients', 'account_box', 'Clients'),
      this.getItem('/taxonomy', 'folder_special', 'Specialties'),
      this.getItem('/procedures', 'local_hospital', 'Procedures'),
      this.getItem('/rvu_benchmarks', 'speed', 'RVU Benchmarks'),
      this.getItem('/upload', 'cloud_upload', 'Upload'),
      this.getItem('/activity', 'history', 'Activity Log'),
      this.getItem('/login_settings', 'network_locked', 'Login Settings'),
    ];
  }

  getClientItems() {
    const { clientMenuOpen } = this.state;
    const { clients, selectedClient } = this.props;

    const drawerOpen = this.drawerOpenOrOnDashboard();
    let client;
    let clientName = '';
    const clientData = StoreUtil.getData(clients);
    if (selectedClient) {
      client = clientData?.find((c) => c.id === parseInt(selectedClient));
    }

    if (client) {
      clientName = client.name;
    }

    if (!drawerOpen) {
      clientName = this.getAbbrieveateName(clientName);
    }

    const showDrawer = clientData.length > 1;

    return [
      <ListItem
        button
        key='client-select'
        onClick={() => {
          if (showDrawer) {
            this.setState({ clientMenuOpen: !clientMenuOpen });
          }
        }}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          padding: 0,
          paddingLeft: 1,
          paddingRight: 1,
          color: (theme) => theme.palette.primary.accent,
          cursor: !showDrawer ? 'default' : 'pointer',
          background: 'transparent',
        }}
        ref={(b) => {
          this.clientButtonRef = b;
        }}
        disableRipple={!showDrawer}
      >
        <>
          <ClientContainer open={drawerOpen}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <Typography
                noWrap
                sx={{
                  fontSize: 12,
                  color: (theme) => theme.palette.primary.accent,
                }}
              >
                {clientName}
              </Typography>
              {clientData.length > 1 && (
                <Icon
                  sx={{
                    fontSize: 12,
                    fontWeight: 300,
                    marginLeft: 1,
                    color: (theme) => theme.palette.primary.accent,
                  }}
                >
                  expand_more
                </Icon>
              )}
            </Box>
          </ClientContainer>
          <Menu
            open={clientMenuOpen}
            anchorEl={this.clientButtonRef}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            onClose={() => {
              this.closeClientMenu();
              this.onDrawerClose();
            }}
          >
            {clientData &&
              clientData.map((c) => (
                <MenuItem
                  value={c.id}
                  key={c.id}
                  onClick={() => {
                    this.setClient(c);
                    this.onDrawerClose();
                  }}
                >
                  {c.name}
                </MenuItem>
              ))}
          </Menu>
        </>
      </ListItem>,
    ].concat([this.getUserItem()]);
  }

  getUserItem = () => {
    const { router, user } = this.props;

    const drawerOpen = this.drawerOpenOrOnDashboard();
    const path = `/users/${user.id}`;
    const itemSelected = router.location.pathname.startsWith(path);

    return (
      <StyledListItem
        button
        key={path}
        component={Link}
        to={path}
        onClick={() => {
          router.navigate(path);
          this.onSelectNavItem();
        }}
        selected={itemSelected}
        sx={{
          paddingLeft: !drawerOpen ? 0 : undefined,
          width: !drawerOpen ? '100%' : undefined,
        }}
      >
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: !drawerOpen ? '100%' : undefined,
              height: 56,
            }}
          >
            <ListItemIcon
              key='icon'
              sx={{ minWidth: drawerOpen ? '56px' : 0 }}
            >
              <StyledListItemIcon selected={itemSelected}>
                account_circle_outline
              </StyledListItemIcon>
            </ListItemIcon>
            {!drawerOpen && (
              <Box
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  display: 'flex',
                  justifyContent: 'center',
                  width: minimizedDrawerWidth - 20,
                }}
              >
                <Typography
                  variant='caption'
                  noWrap
                  sx={{ fontSize: 11, textAlign: 'center' }}
                >
                  {user.first_name} {user.last_name}
                </Typography>
              </Box>
            )}
          </Box>
          {drawerOpen && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'start',
                wordWrap: 'break-word',
                alignSelf: 'start',
                marginLeft: -2,
              }}
            >
              <Box
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  width: minimizedDrawerWidth + 40,
                }}
              >
                <Typography
                  variant='caption'
                  noWrap
                  sx={{ fontSize: 11 }}
                >
                  {user.first_name} {user.last_name}
                </Typography>
              </Box>
              <Typography
                variant='button'
                fontSize={10}
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  this.onLogout();
                }}
                sx={{
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                  color: '#FFFFFF',
                }}
              >
                Logout
              </Typography>
            </Box>
          )}
          {itemSelected && !drawerOpen && (
            <SelectedWedgeIcon
              sx={{ position: 'absolute', right: 0 }}
            >
              arrow_left
            </SelectedWedgeIcon>
          )}
        </>
      </StyledListItem>
    );
  };

  getAccountItems = () => {
    return [
      this.getItem(
        '/support',
        'support_agent',
        'Support',
        false,
        this.onContactSupport
      ),
    ];
  };

  getAIItems = () => {
    return [
      this.getItem(
        '/support',
        'support_agent',
        'Support',
        false,
        this.onContactSupport
      ),
    ];
  };

  getAbbrieveateName(clientName) {
    const words = clientName.split(' ');
    let abbr = '';
    if (words.length > 1) {
      words.forEach((word) => {
        abbr += word.substring(0, 1);
      });
    } else {
      abbr = clientName.substring(0, 3);
    }
    return abbr;
  }

  closeClientMenu = () => {
    this.setState({
      clientMenuOpen: false,
    });
  };

  drawerOpenOrOnDashboard = () => {
    const { router } = this.props;
    const { drawerOpen } = this.state;

    return (
      drawerOpen ||
      router.location.pathname === '/' ||
      router.location.pathname.startsWith('/dashboard')
    );
  };
}

AppNavigation.propTypes = {
  router: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  selectedClient: PropTypes.number,
  user: PropTypes.object,
  clients: PropTypes.object,
  app: PropTypes.object,
  isMobile: PropTypes.bool.isRequired,
  setOpenChat: PropTypes.func,
  setClicked: PropTypes.func,
};

function mapStateToProps(state) {
  return {
    app: state.app,
    user: state.user,
    selectedClient: state.app.selectedClient,
    clients: StoreUtil.get(state.clients, StoreUtil.COMMON_LIST),
  };
}

const styledApp = withIsMobile(AppNavigation);

const connected = connect(
  mapStateToProps,
  null,
  null,
  { forwardRef: true }
)(styledApp);

const routed = withRouter(connected);

export { routed as AppNavigation };
export default routed;